import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  TextField,
  MenuItem,
  Divider
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { parseISO, format, eachDayOfInterval, isValid } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DEFAULT_TIME_BLOCKS, SESSION_TYPES, GENDER_OPTIONS, LEVEL_OPTIONS, } from '../../constants/competitionConstants';





const SessionStructureForm = ({ competition, onUpdate }) => {
  const competitionDays = useMemo(() => {
    if (!competition.startDate || !competition.endDate) return [];
    
    // Handle dates whether they're Date objects or ISO strings
    const start = competition.startDate instanceof Date ? 
      competition.startDate : 
      parseISO(competition.startDate);
      
    const end = competition.endDate instanceof Date ? 
      competition.endDate : 
      parseISO(competition.endDate);
    
    if (!isValid(start) || !isValid(end)) return [];
    
    return eachDayOfInterval({ start, end })
      .map(date => format(date, 'yyyy-MM-dd'));
  }, [competition.startDate, competition.endDate]);

  const sessionsByDay = useMemo(() => {
    return (competition.sessions || []).reduce((acc, session) => {
      const day = session.startDate instanceof Date ? 
        format(session.startDate, 'yyyy-MM-dd') :
        session.startDate.split(' ')[0];
      if (!acc[day]) acc[day] = [];
      acc[day].push(session);
      return acc;
    }, {});
  }, [competition.sessions]);

  const handleAddSession = (day) => {
    // Find the last session of the same day to copy settings from
    const previousSession = (sessionsByDay[day] || []).slice(-1)[0];
    
    let timeBlocks = {};
    let enabledTimeBlocks = [];

    if (previousSession) {
      // Copy enabled blocks from previous session
      enabledTimeBlocks = [...previousSession.enabledTimeBlocks];
      
      // Create a mapping of relative times (in minutes) from the base time
      const timeOffsets = {
        openStretchStart: -90,  // 1.5 hours before competition
        warmUpStart: -60,       // 1 hour before competition
        marchInStart: -15,      // 15 minutes before competition
        competitionStart: 0,    // base time
        competitionEnd: 120,    // 2 hours after competition start
        awardsEnd: 150         // 2.5 hours after competition start
      };

      // Get base time and validate
      const baseTime = previousSession?.timeBlocks.competitionStart || `${day} 09:00:00`;
      let baseDate = parseISO(baseTime);
      
      // Fallback to 9 AM if the base time is invalid
      if (!isValid(baseDate)) {
        baseDate = parseISO(`${day} 09:00:00`);
      }

      // Apply the offsets to create properly sequenced times
      DEFAULT_TIME_BLOCKS.forEach(block => {
        if (enabledTimeBlocks.includes(block.id)) {
          const offset = timeOffsets[block.id];
          if (offset !== undefined) {
            const newTime = new Date(baseDate.getTime() + offset * 60000); // convert minutes to milliseconds
            timeBlocks[block.id] = format(newTime, "yyyy-MM-dd HH:mm:ss");
          }
        }
      });
    }

    const newSession = {
      id: crypto.randomUUID(),
      sessionNumber: (competition.sessions || []).length + 1,
      title: `Session ${(competition.sessions || []).length + 1}`,
      type: previousSession?.type || 'Traditional',
      gender: previousSession?.gender || '',
      levels: [], // Reset levels for new session
      gymnastCount: 0,
      startDate: timeBlocks.competitionStart || `${day} 09:00:00`,
      endDate: timeBlocks.competitionEnd || `${day} 11:00:00`,
      timeBlocks,
      enabledTimeBlocks
    };

    onUpdate({
      ...competition,
      sessions: [...(competition.sessions || []), newSession]
    });

    // Scroll the new session into view
    setTimeout(() => {
      const newSessionElement = document.getElementById(`session-${newSession.id}`);
      if (newSessionElement) {
        newSessionElement.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100); // Small delay to ensure DOM is updated
  };

  const handleUpdateSession = (sessionId, updates) => {
    const updatedSessions = (competition.sessions || []).map(session =>
      session.id === sessionId ? { ...session, ...updates } : session
    );

    onUpdate({
      ...competition,
      sessions: updatedSessions
    });
  };

  const handleDeleteSession = (sessionId) => {
    const updatedSessions = (competition.sessions || [])
      .filter(session => session.id !== sessionId)
      .map((session, index) => ({
        ...session,
        sessionNumber: index + 1
      }));

    onUpdate({
      ...competition,
      sessions: updatedSessions
    });
  };

  const handleTimeChange = (sessionId, blockType, newTime) => {
    if (!newTime || !isValid(newTime)) return;

    const session = competition.sessions.find(s => s.id === sessionId);
    if (!session) return;

    const timeStr = format(newTime, "yyyy-MM-dd HH:mm:ss");
    const updatedTimeBlocks = {
      ...session.timeBlocks,
      [blockType]: timeStr
    };

    const updates = {
      timeBlocks: updatedTimeBlocks
    };

    if (blockType === 'competitionStart') {
      updates.startDate = timeStr;
    } else if (blockType === 'competitionEnd') {
      updates.endDate = timeStr;
    }

    handleUpdateSession(sessionId, updates);
  };

  const handleToggleTimeBlock = (sessionId, blockId) => {
    const session = competition.sessions.find(s => s.id === sessionId);
    if (!session) return;

    const enabledBlocks = session.enabledTimeBlocks || [];
    const timeBlocks = { ...session.timeBlocks };
    
    if (enabledBlocks.includes(blockId)) {
      delete timeBlocks[blockId];
      handleUpdateSession(sessionId, {
        timeBlocks,
        enabledTimeBlocks: enabledBlocks.filter(id => id !== blockId)
      });
    } else {
      const baseTime = timeBlocks.competitionStart || `${session.startDate.split(' ')[0]} 09:00:00`;
      timeBlocks[blockId] = baseTime;
      handleUpdateSession(sessionId, {
        timeBlocks,
        enabledTimeBlocks: [...enabledBlocks, blockId]
      });
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Session Structure
      </Typography>
      
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        gap: 2,
        width: '100%'
      }}>
        {competitionDays.map(day => (
          <Card key={day} sx={{ height: '100%' }}>
              <CardHeader
                title={format(parseISO(day), 'EEEE, MMMM d')}
                titleTypographyProps={{ variant: 'h6' }}
              />
              <Divider />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(sessionsByDay[day] || []).map((session) => (
                    <Card 
                      key={session.id}
                      id={`session-${session.id}`}
                      variant="outlined"
                      sx={{
                        '&:hover': {
                          boxShadow: 1,
                        }
                      }}
                    >
                      <CardHeader
                        title={`Session ${session.sessionNumber}`}
                        titleTypographyProps={{ variant: 'subtitle1' }}
                        action={
                          <IconButton
                            data-ui={`btn-delete-session-${session.sessionNumber}`}
                            onClick={() => handleDeleteSession(session.id)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                        sx={{ py: 1 }}
                      />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            data-ui={`form-session-title-${session.sessionNumber}`}
                            fullWidth
                            label="Session Title"
                            value={session.title}
                            onChange={(e) => handleUpdateSession(session.id, { title: e.target.value })}
                            sx={{ mb: 1 }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            data-ui={`form-session-type-${session.sessionNumber}`}
                            select
                            fullWidth
                            label="Session Type"
                            value={session.type}
                            onChange={(e) => handleUpdateSession(session.id, { type: e.target.value })}
                            sx={{ mb: 1 }}
                          >
                            {SESSION_TYPES.map((type) => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            data-ui={`form-session-gender-${session.sessionNumber}`}
                            select
                            fullWidth
                            label="Gender"
                            value={session.gender}
                            onChange={(e) => handleUpdateSession(session.id, { gender: e.target.value })}
                            sx={{ mb: 2 }}
                          >
                            {GENDER_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            data-ui={`form-session-levels-${session.sessionNumber}`}
                            select
                            fullWidth
                            label="Levels"
                            value={session.levels}
                            SelectProps={{
                              multiple: true,
                              renderValue: (selected) => selected
                                .map(value => LEVEL_OPTIONS.find(opt => opt.value === value)?.label || value)
                                .join(', '),
                              onKeyDown: (event) => {
                                // Only handle number keys for level selection
                                if (!/^[0-9]$/.test(event.key)) return;
                                
                                const number = parseInt(event.key, 10);
                                
                                // Filter levels based on current gender selection
                                const availableLevels = LEVEL_OPTIONS.filter(
                                  level => !session.gender || level.gender === session.gender
                                );
                                
                                // Find the closest level to the pressed number
                                const closestLevel = availableLevels.find(level => {
                                  // Extract number from level value (e.g., "L8" -> 8)
                                  const levelNum = parseInt(level.value.replace(/[^0-9]/g, ''), 10);
                                  return levelNum === number;
                                });
                                
                                if (closestLevel) {
                                  event.preventDefault();
                                  // Toggle the level selection
                                  const currentLevels = session.levels || [];
                                  const newLevels = currentLevels.includes(closestLevel.value)
                                    ? currentLevels.filter(l => l !== closestLevel.value)
                                    : [...currentLevels, closestLevel.value];
                                    
                                  handleUpdateSession(session.id, { levels: newLevels });
                                }
                              }
                            }}
                            onChange={(e) => handleUpdateSession(session.id, { 
                              levels: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value 
                            })}
                            sx={{ mb: 2 }}
                          >
                            {LEVEL_OPTIONS
                              .filter(level => !session.gender || level.gender === session.gender)
                              .map((level) => (
                                <MenuItem key={level.value} value={level.value}>
                                  {level.label}
                                </MenuItem>
                              ))}
                          </TextField>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mb: 1
                            }}
                          >
                            <AccessTimeIcon sx={{ mr: 1, fontSize: 20 }} />
                            Time Schedule
                          </Typography>
                          
                          <Box sx={{ 
                            mb: 2,
                            p: 1.5,
                            bgcolor: 'background.default',
                            borderRadius: 1
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Select time blocks to include:
                            </Typography>
                            {DEFAULT_TIME_BLOCKS.map((block) => (
                              <Button
                                data-ui={`btn-toggle-timeblock-${block.id}-${session.sessionNumber}`}
                                key={block.id}
                                size="small"
                                variant={(session.enabledTimeBlocks || []).includes(block.id) ? "contained" : "outlined"}
                                onClick={() => handleToggleTimeBlock(session.id, block.id)}
                                sx={{ mr: 1, mb: 1 }}
                              >
                                {block.label}
                              </Button>
                            ))}
                          </Box>
                          
                          <Grid container spacing={2}>
                            {DEFAULT_TIME_BLOCKS
                              .filter(block => (session.enabledTimeBlocks || []).includes(block.id))
                              .map(block => (
                                <Grid item xs={12} sm={6} key={block.id}>
                                  <TimePicker
                                    data-ui={`form-session-time-${block.id}-${session.sessionNumber}`}
                                    label={block.label}
                                    value={session.timeBlocks[block.id] ? parseISO(session.timeBlocks[block.id]) : null}
                                    onChange={(newValue) => handleTimeChange(session.id, block.id, newValue)}
                                    slotProps={{ textField: { required: false } }}
                                  />
                                </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  ))}
                  {!sessionsByDay[day]?.length && (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No sessions scheduled
                    </Typography>
                  )}
                </Box>
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  data-ui={`btn-add-session-${day}`}
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => handleAddSession(day)}
                  variant="outlined"
                  size="medium"
                >
                  Add Session
                </Button>
              </Box>
            </Card>
        ))}
      </Box>
    </Box>
  );
};
 

export default SessionStructureForm;
